import { useEffect, useLayoutEffect, useRef } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '@ramsey-design-system/input';
import FormField from '@ramsey-design-system/form-field';
import Button from '@ramsey-design-system/button';
import { useWizard } from 'react-use-wizard';
import { useGAFormTracking } from '../../../shared/hooks';
import { useMatcherContext } from '../context';
import { getAnsweredString } from '../flow/types';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import {
  getMatcherFormName,
  useMatcherButtonSize,
  useMatcherFieldSize,
  useTrackMatcherStepStarted
} from '../utils';
import {
  MatcherErrorMessage,
  MatcherProAgreementText,
  MatcherStandardStepTitle,
  MatcherStepWrapper
} from '../components';
import { matcherEmailSchema } from '../schemas/MatcherEmailSchema';

const STEP_NAME = 'matcher_email_step';
const STEP_LABEL = "Last thing. Where can we send your pro's info?";

interface MatcherEmailStepFields {
  email?: string | null;
}

function MatcherEmailStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const { activeStep } = useWizard();
  const {
    matcherAnswers,
    createMatcherReferral,
    referralCreation,
    retryReferralCreation,
    clearReferralCreationError,
    matchSearchState
  } = useMatcherContext();
  const isCreating = referralCreation.status === 'creating';
  const hasError = referralCreation.status === 'error';
  const proName = matchSearchState.topMatch?.headProName;
  const fieldSize = useMatcherFieldSize();
  const buttonSize = useMatcherButtonSize();

  // Clears a stale error before first paint so returning to this step never flashes it.
  useLayoutEffect(() => {
    clearReferralCreationError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<MatcherEmailStepFields>({
    defaultValues: {
      email: getAnsweredString(matcherAnswers.email)
    },
    resolver: yupResolver(matcherEmailSchema)
  });

  useTrackMatcherStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  const previousHasError = useRef(hasError);
  useEffect(() => {
    if (hasError && !previousHasError.current) {
      trackStepErrorViewed({
        step: activeStep + 1,
        step_name: STEP_NAME,
        step_label: STEP_LABEL,
        error_type: 'system',
        error_message: referralCreation.errorMessage ?? '',
        form_name: getMatcherFormName()
      });
    }
    previousHasError.current = hasError;
  }, [hasError, activeStep, referralCreation.errorMessage]);

  function onValidSubmit(values: MatcherEmailStepFields) {
    if (!createMatcherReferral(values.email || null)) return;
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['Email'],
      answer_values: [values.email ?? ''],
      form_name: getMatcherFormName()
    });
  }

  function onInvalidSubmit(errors: FieldErrors<MatcherEmailStepFields>) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      error_type: 'validation',
      error_field: 'email',
      error_message: errors.email?.message ?? '',
      form_name: getMatcherFormName()
    });
  }

  return (
    <MatcherStepWrapper>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        noValidate
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle noSubtitle>
          Last thing. Where can we send your pro&apos;s info?
        </MatcherStandardStepTitle>

        <div className="space-y-2 mb-4">
          <FormField
            id="email"
            label="Email"
            note="(Optional)"
            size={fieldSize}
            invalid={!!errors.email}
            errors={[errors.email?.message]}
          >
            {() => (
              <Input
                {...register('email')}
                type="email"
                required={false}
                invalid={!!errors.email}
                size={fieldSize}
                id="email"
                data-testid="tax_matcher_email_input"
                autoComplete="email"
              />
            )}
          </FormField>
        </div>

        {hasError && (
          <MatcherErrorMessage errorMessage={referralCreation.errorMessage} />
        )}

        <MatcherProAgreementText proName={proName} />

        <div className="mt-auto md:mt-0">
          {hasError ? (
            <Button
              data-testid="matcher_email_try_again_button"
              type="button"
              className={'!w-full'}
              fullWidthOnMobile={true}
              size={buttonSize}
              onClick={retryReferralCreation}
            >
              See My Pro
            </Button>
          ) : (
            <Button
              data-testid="matcher_see_my_pro_button"
              type="submit"
              className={'!w-full'}
              fullWidthOnMobile={true}
              size={buttonSize}
              loading={isCreating}
              disabled={isCreating}
            >
              {isCreating ? 'Connecting . . .' : 'See My Pro'}
            </Button>
          )}
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherEmailStep };
